/**
 * Auto-routing Models
 *
 * NOTE: These are app-specific types for automatic ticket routing.
 * There are no corresponding library types in @open/nexus-public-api.
 *
 * This module provides:
 * - Routing rules with conditions and actions
 * - Processing queues with assignment strategies
 * - Agent skill-based routing
 * - Default routing rule templates
 */

import type { TicketMainCategory, TicketSubCategory } from '../../ticket-category'

// Правило маршрутизации
export interface RoutingRule {
  id: string
  name: string
  nameRu: string
  description?: string
  priority: number // Чем меньше число, тем выше приоритет

  // Условия срабатывания
  conditions: RoutingCondition[]

  // Действия при срабатывании
  actions: RoutingAction[]

  // Настройки
  isActive: boolean
  createdAt: number
  updatedAt: number
}

// Условие маршрутизации
export interface RoutingCondition {
  type: RoutingConditionType
  operator: RoutingOperator
  value: string | string[] | number
}

// Типы условий
export enum RoutingConditionType {
  CATEGORY = 'category',                   // Категория обращения
  SUBCATEGORY = 'subcategory',             // Подкатегория
  PRIORITY = 'priority',                   // Приоритет
  CHANNEL = 'channel',                     // Канал обращения (phone, email, chat)
  CUSTOMER_SEGMENT = 'customer_segment',   // Сегмент клиента
  TIME_OF_DAY = 'time_of_day',            // Время суток
  DAY_OF_WEEK = 'day_of_week',            // День недели
  KEYWORDS = 'keywords',                   // Ключевые слова в описании
  ACCOUNT_STATUS = 'account_status'        // Статус аккаунта клиента
}

// Операторы сравнения
export enum RoutingOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  IN = 'in',
  NOT_IN = 'not_in',
  CONTAINS = 'contains',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than'
}

// Действие маршрутизации
export interface RoutingAction {
  type: RoutingActionType
  value: string | string[]
}

// Типы действий
export enum RoutingActionType {
  ASSIGN_TO_QUEUE = 'assign_to_queue',     // Назначить в очередь
  ASSIGN_TO_AGENT = 'assign_to_agent',     // Назначить агенту
  SET_PRIORITY = 'set_priority',           // Установить приоритет
  ADD_TAG = 'add_tag',                     // Добавить тег
  SEND_NOTIFICATION = 'send_notification'  // Отправить уведомление
}

// Результат маршрутизации
export interface RoutingResult {
  ruleId: string
  ruleName: string
  queueId?: string
  agentId?: string
  priority?: 'high' | 'mid' | 'low'
  tags?: string[]
  notifications?: string[]
}

// Запрос на маршрутизацию
export interface RoutingRequest {
  // Данные обращения
  category?: TicketMainCategory
  subcategory?: TicketSubCategory
  priority?: 'high' | 'mid' | 'low'
  channel?: string
  description?: string

  // Данные клиента
  customerId?: string
  customerSegment?: string
  accountStatus?: string

  // Контекст
  timestamp?: number
}

// Очередь обработки
export interface ProcessingQueue {
  id: string
  name: string
  nameRu: string
  description?: string

  // Настройки
  maxCapacity?: number               // Максимальное количество обращений
  currentLoad: number                // Текущая загрузка

  // Агенты в очереди
  agentIds: string[]

  // Автоназначение
  autoAssign: boolean
  assignmentStrategy: AssignmentStrategy

  isActive: boolean
  createdAt: number
  updatedAt: number
}

// Стратегии назначения
export enum AssignmentStrategy {
  ROUND_ROBIN = 'round_robin',       // По кругу
  LEAST_LOADED = 'least_loaded',     // Наименее загруженному
  SKILL_BASED = 'skill_based',       // По навыкам
  RANDOM = 'random'                  // Случайное
}

// Навыки агента
export interface AgentSkill {
  agentId: string
  skills: string[]                   // Список навыков (категории, языки, и т.д.)
  proficiencyLevel: Record<string, number> // Уровень владения (1-5)
}

// Локализация типов условий
export const ROUTING_CONDITION_TYPE_LABELS: Record<RoutingConditionType, string> = {
  [RoutingConditionType.CATEGORY]: 'Категория',
  [RoutingConditionType.SUBCATEGORY]: 'Подкатегория',
  [RoutingConditionType.PRIORITY]: 'Приоритет',
  [RoutingConditionType.CHANNEL]: 'Канал обращения',
  [RoutingConditionType.CUSTOMER_SEGMENT]: 'Сегмент клиента',
  [RoutingConditionType.TIME_OF_DAY]: 'Время суток',
  [RoutingConditionType.DAY_OF_WEEK]: 'День недели',
  [RoutingConditionType.KEYWORDS]: 'Ключевые слова',
  [RoutingConditionType.ACCOUNT_STATUS]: 'Статус аккаунта'
}

// Локализация операторов
export const ROUTING_OPERATOR_LABELS: Record<RoutingOperator, string> = {
  [RoutingOperator.EQUALS]: 'Равно',
  [RoutingOperator.NOT_EQUALS]: 'Не равно',
  [RoutingOperator.IN]: 'Входит в',
  [RoutingOperator.NOT_IN]: 'Не входит в',
  [RoutingOperator.CONTAINS]: 'Содержит',
  [RoutingOperator.GREATER_THAN]: 'Больше чем',
  [RoutingOperator.LESS_THAN]: 'Меньше чем'
}

// Локализация типов действий
export const ROUTING_ACTION_TYPE_LABELS: Record<RoutingActionType, string> = {
  [RoutingActionType.ASSIGN_TO_QUEUE]: 'Назначить в очередь',
  [RoutingActionType.ASSIGN_TO_AGENT]: 'Назначить агенту',
  [RoutingActionType.SET_PRIORITY]: 'Установить приоритет',
  [RoutingActionType.ADD_TAG]: 'Добавить тег',
  [RoutingActionType.SEND_NOTIFICATION]: 'Отправить уведомление'
}

// Локализация стратегий
export const ASSIGNMENT_STRATEGY_LABELS: Record<AssignmentStrategy, string> = {
  [AssignmentStrategy.ROUND_ROBIN]: 'По кругу',
  [AssignmentStrategy.LEAST_LOADED]: 'Наименее загруженному',
  [AssignmentStrategy.SKILL_BASED]: 'По навыкам',
  [AssignmentStrategy.RANDOM]: 'Случайное'
}

// Дефолтные правила маршрутизации
export const DEFAULT_ROUTING_RULES: Omit<RoutingRule, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'High Priority to VIP Queue',
    nameRu: 'Высокий приоритет в VIP очередь',
    priority: 1,
    conditions: [
      {
        type: RoutingConditionType.PRIORITY,
        operator: RoutingOperator.EQUALS,
        value: 'high'
      }
    ],
    actions: [
      {
        type: RoutingActionType.ASSIGN_TO_QUEUE,
        value: 'vip-queue'
      }
    ],
    isActive: true
  },
  {
    name: 'Complaints to Specialist Queue',
    nameRu: 'Претензии в очередь специалистов',
    priority: 2,
    conditions: [
      {
        type: RoutingConditionType.CATEGORY,
        operator: RoutingOperator.IN,
        value: ['complaints_oral', 'complaints_written']
      }
    ],
    actions: [
      {
        type: RoutingActionType.ASSIGN_TO_QUEUE,
        value: 'complaints-queue'
      },
      {
        type: RoutingActionType.ADD_TAG,
        value: ['complaint', 'requires-attention']
      }
    ],
    isActive: true
  },
  {
    name: 'After Hours to Night Shift',
    nameRu: 'Вне рабочих часов в ночную смену',
    priority: 3,
    conditions: [
      {
        type: RoutingConditionType.TIME_OF_DAY,
        operator: RoutingOperator.GREATER_THAN,
        value: 18
      }
    ],
    actions: [
      {
        type: RoutingActionType.ASSIGN_TO_QUEUE,
        value: 'night-shift-queue'
      }
    ],
    isActive: true
  }
]
